import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { Users as UsersIcon } from 'lucide-react';
import { AdminRoleSwitch } from '@/components/admin-role-switch';
import { DataTable, DataTableColumnHeader } from '@/components/data-table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePermissions } from '@/hooks/use-permissions';
import { useStaff } from '@/hooks/use-staff';
import { ROLE_LABELS } from '@/hooks/use-user-role-mutations';
import { PERMISSIONS, type OrgUserWithRole } from '@property/shared';
import { useTableRefresh } from '@/hooks/use-table-refresh';
import { queryKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/dashboard/users/')({
  component: UsersPage,
});

const PAGE_SIZE = 10;

const staffColumns: ColumnDef<OrgUserWithRole>[] = [
  {
    accessorKey: 'name',
    enableSorting: false,
    meta: { label: 'Name', isPrimary: true, isMandatory: true },
    header: () => <span>Name</span>,
    cell: ({ row }) => {
      const name = row.original.name ?? row.original.email;
      const initial = name.charAt(0).toUpperCase();
      return (
        <div className="flex items-center gap-3">
          <Avatar className="size-8 rounded-lg">
            <AvatarImage src={row.original.profilePictureUrl ?? undefined} alt={name} />
            <AvatarFallback className="rounded-lg text-xs">{initial}</AvatarFallback>
          </Avatar>
          <span className="font-medium text-foreground">{row.original.name ?? '—'}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'email',
    enableSorting: false,
    meta: { label: 'Email' },
    header: () => <span>Email</span>,
    cell: ({ row }) => <span className="text-muted-foreground">{row.original.email}</span>,
  },
  {
    accessorKey: 'role',
    enableSorting: true,
    meta: { label: 'Role' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Role" />,
    cell: ({ row }) => (
      <span className="text-muted-foreground">{ROLE_LABELS[row.original.role]}</span>
    ),
  },
];

function StaffTable() {
  const refresh = useTableRefresh(queryKeys.users.all());
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { data, total, isLoading, isFetching } = useStaff({
    viewPage: page,
    viewPageSize: PAGE_SIZE,
    sortBy,
    sortOrder,
  });

  return (
    <div id="users-staff" className="space-y-3 scroll-mt-32">
      <h2 className="text-sm font-semibold uppercase tracking-[0.28em] text-ring">Staff</h2>
      <DataTable
        tableId="users-staff"
        showColumnToggle
        onRefresh={refresh}
        columns={staffColumns}
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total,
          onPageChange: setPage,
        }}
        sorting={{
          sortBy,
          sortOrder,
          onSortChange: (by, order) => {
            setSortBy(by);
            setSortOrder(order ?? 'desc');
            setPage(1);
          },
        }}
        emptyState={{
          icon: <UsersIcon className="w-5 h-5 text-muted-foreground" />,
          title: 'No staff yet',
          description: 'Assign users a staff role via the role switcher above.',
        }}
      />
    </div>
  );
}

function UsersPage() {
  const { can } = usePermissions();
  return (
    <section className="space-y-8">
      <h1 className="font-display text-3xl font-medium tracking-tight text-foreground">Users</h1>
      {can(PERMISSIONS.ROLES_ASSIGN) && (
        <>
          <div id="users-assign-roles" className="space-y-3 scroll-mt-32">
            <h2 className="text-sm font-semibold uppercase tracking-[0.28em] text-ring">
              Assign Roles
            </h2>
            <AdminRoleSwitch />
          </div>
          <StaffTable />
        </>
      )}
    </section>
  );
}
